import React from 'react';
import { FileSpreadsheet, Upload, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';

const REQUIRED_FIELDS = [
  { key: "email", label: "Email", description: "Email address (required)", required: true },
];

const OPTIONAL_FIELDS = [
  { key: "name", label: "Full Name", description: "Contact person's full name" },
  { key: "first_name", label: "First Name", description: "Contact's first name" },
  { key: "last_name", label: "Last Name", description: "Contact's last name" },
  { key: "business_name", label: "Business Name", description: "Company/business name" },
  { key: "location", label: "Location", description: "City or area" },
  { key: "address", label: "Address", description: "Full street address" },
  { key: "postcode", label: "Postcode", description: "Postal/ZIP code" },
  { key: "category", label: "Category", description: "Trade category (e.g., Plumber)" },
  { key: "website", label: "Website", description: "Business website URL" },
  { key: "phone_number", label: "Phone Number", description: "Contact phone number" },
  { key: "company_phone_number", label: "Company Phone", description: "Business phone number" },
];

export const ColumnMappingDialog = ({
  open,
  onOpenChange,
  previewData,
  columnMapping,
  onColumnMappingChange,
  onUpload,
  uploading,
  onClose
}) => {
  const getMappedValue = (fieldKey) => {
    if (!previewData?.sample_data?.[0] || !columnMapping[fieldKey]) return "-";
    return previewData.sample_data[0][columnMapping[fieldKey]] || "-";
  };

  const handleClose = () => {
    onClose();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(openState) => {
      if (!openState) handleClose();
      else onOpenChange(openState);
    }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Map Columns
          </DialogTitle>
          <DialogDescription>
            Match your spreadsheet columns to contact fields. {previewData?.total_rows} rows found.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-6 max-h-[60vh] overflow-y-auto">
          {/* Required Fields */}
          <div>
            <Label className="text-sm font-semibold text-slate-900 mb-3 block">Required Field</Label>
            <div className="space-y-3">
              {REQUIRED_FIELDS.map((field) => (
                <div key={field.key} className="flex items-center gap-4 p-3 bg-orange-50 rounded-md border border-orange-200">
                  <div className="w-40">
                    <Label className="font-medium text-slate-900">{field.label} *</Label>
                    <p className="text-xs text-slate-500">{field.description}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <Select
                    value={columnMapping[field.key] || ""}
                    onValueChange={(value) => onColumnMappingChange({ ...columnMapping, [field.key]: value })}
                  >
                    <SelectTrigger className="w-48" data-testid={`map-${field.key}`}>
                      <SelectValue placeholder="Select column" />
                    </SelectTrigger>
                    <SelectContent>
                      {previewData?.columns?.filter(col => col && col.trim() !== "").map((col) => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex-1 min-w-0">
                    {columnMapping[field.key] && (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <span className="text-sm text-slate-600 truncate">
                          Preview: {getMappedValue(field.key)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Optional Fields */}
          <div>
            <Label className="text-sm font-semibold text-slate-900 mb-3 block">Optional Fields</Label>
            <div className="space-y-2">
              {OPTIONAL_FIELDS.map((field) => (
                <div key={field.key} className="flex items-center gap-4 p-3 bg-slate-50 rounded-md border border-slate-200">
                  <div className="w-40">
                    <Label className="font-medium text-slate-700">{field.label}</Label>
                    <p className="text-xs text-slate-500">{field.description}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <Select
                    value={columnMapping[field.key] || "_none_"}
                    onValueChange={(value) => onColumnMappingChange({ ...columnMapping, [field.key]: value === "_none_" ? "" : value })}
                  >
                    <SelectTrigger className="w-48" data-testid={`map-${field.key}`}>
                      <SelectValue placeholder="Skip this field" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">Skip this field</SelectItem>
                      {previewData?.columns?.map((col) => (
                        <SelectItem key={col} value={col}>{col}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex-1 min-w-0">
                    {columnMapping[field.key] && (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <span className="text-sm text-slate-600 truncate">
                          Preview: {getMappedValue(field.key)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sample Data Preview */}
          {previewData?.sample_data?.length > 0 && (
            <div>
              <Label className="text-sm font-semibold text-slate-900 mb-2 block">
                Sample Data Preview (First 3 rows)
              </Label>
              <div className="border border-slate-200 rounded-md overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      {previewData.columns.map((col) => (
                        <TableHead key={col} className="text-xs font-medium text-slate-600 whitespace-nowrap">
                          {col}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.sample_data.map((row, idx) => (
                      <TableRow key={`row-${idx}-${Object.values(row).join('-').slice(0, 50)}`}>
                        {previewData.columns.map((col) => (
                          <TableCell key={col} className="text-sm text-slate-600 whitespace-nowrap">
                            {row[col] || "-"}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={onUpload}
            disabled={uploading || REQUIRED_FIELDS.filter(f => f.required).some(f => !columnMapping[f.key])}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="confirm-upload-btn"
          >
            {uploading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Uploading...
              </span>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload {previewData?.total_rows} Contacts
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { REQUIRED_FIELDS, OPTIONAL_FIELDS };
export default ColumnMappingDialog;
